import React, { useState, useEffect } from 'react';
import { backgroundProcessService } from '../../../services/api';
import styles from './BackgroundProcessModal.module.css';

const BackgroundProcessModal = ({ isOpen, onClose }) => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const fetchProcesses = async () => {
    try {
      setError(null);
      const response = await backgroundProcessService.getUserProcesses();
      if (response.data && response.data.status === 'success') {
        setProcesses(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching background processes:', err);
      setError('Failed to load background processes');
    } finally {
      setLoading(false);
    }
  };

  const refreshProcesses = async () => {
    setRefreshing(true);
    await fetchProcesses();
    setRefreshing(false);
  };

  const handleCancelProcess = async (processId) => {
    try {
      await backgroundProcessService.cancelProcess(processId);
      // Refresh the list after cancellation
      await fetchProcesses();
    } catch (err) {
      console.error('Error cancelling process:', err);
      setError('Failed to cancel process');
    }
  };

  // Filter and limit processes
  const getFilteredProcesses = () => {
    let filtered = processes;
    
    // Filter by status
    if (!showCompleted) {
      filtered = filtered.filter(process => process.status === 'running');
    }
    
    // Sort by started_at (most recent first) and limit to 10
    filtered = filtered
      .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
      .slice(0, 10);
    
    return filtered;
  };

  useEffect(() => {
    if (isOpen) {
      fetchProcesses();
    }
  }, [isOpen]);

  // Auto-refresh every 10 seconds when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      fetchProcesses();
    }, 10000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <i className="fas fa-spinner fa-spin" style={{ color: '#0077cc' }}></i>;
      case 'completed':
        return <i className="fas fa-check-circle" style={{ color: '#00cc88' }}></i>;
      case 'failed':
        return <i className="fas fa-exclamation-circle" style={{ color: '#ff6b6b' }}></i>;
      case 'cancelled':
        return <i className="fas fa-times-circle" style={{ color: '#ffa500' }}></i>;
      default:
        return <i className="fas fa-question-circle" style={{ color: '#999' }}></i>;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const getProcessTypeText = (processType) => {
    switch (processType) {
      case 'premium_analysis':
        return 'Premium Analysis';
      case 'file_processing':
        return 'File Processing';
      case 'ai_analysis':
        return 'AI Analysis';
      case 'data_migration':
        return 'Data Migration';
      default:
        return processType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  // Helper function to format current step text
  const formatCurrentStep = (currentStep, status) => {
    if (status !== 'running') {
      return null; // Don't show current step for non-running processes
    }

    if (!currentStep) {
      return 'Initializing...';
    }

    // Clean up common step messages
    const step = currentStep.trim();
    
    // Remove redundant prefixes
    if (step.startsWith('Completed ')) {
      return step.substring(10); // Remove "Completed " prefix
    }
    
    if (step.startsWith('Running ')) {
      return step.substring(8); // Remove "Running " prefix
    }
    
    if (step.startsWith('Starting ')) {
      return step.substring(9); // Remove "Starting " prefix
    }

    return step;
  };

  const formatDuration = (startedAt, completedAt = null) => {
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs}s`;
    }
    return `${diffSecs}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  const filteredProcesses = getFilteredProcesses();
  const runningCount = processes.filter(p => p.status === 'running').length;
  const completedCount = processes.filter(p => p.status === 'completed').length;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Background Processes</h2>
          <div className={styles.headerActions}>
            <button 
              className={`${styles.filterButton} ${showCompleted ? styles.active : ''}`}
              onClick={() => setShowCompleted(!showCompleted)}
              title={showCompleted ? "Show only running processes" : "Show completed processes"}
            >
              <i className={`fas ${showCompleted ? 'fa-eye-slash' : 'fa-history'}`}></i>
              {showCompleted ? 'Hide Completed' : 'Show Completed'}
            </button>
            <button 
              className={styles.refreshButton}
              onClick={refreshProcesses}
              disabled={refreshing}
            >
              <i className={`fas fa-sync-alt ${refreshing ? styles.spinning : ''}`}></i>
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className={styles.modalContent}>
          {/* Status summary */}
          <div className={styles.statusSummary}>
            <span className={styles.runningCount}>
              <i className={`fas ${runningCount > 0 ? 'fa-spinner fa-spin' : 'fa-clock'}`}></i>
              {runningCount} Running
            </span>
            {showCompleted && (
              <span className={styles.completedCount}>
                <i className="fas fa-check-circle"></i>
                {completedCount} Completed
              </span>
            )}
          </div>

          {loading ? (
            <div className={styles.loading}>
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading processes...</span>
            </div>
          ) : error ? (
            <div className={styles.error}>
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          ) : filteredProcesses.length === 0 ? (
            <div className={styles.empty}>
              <i className="fas fa-check-circle"></i>
              <span>
                {showCompleted 
                  ? 'No background processes found' 
                  : 'No running processes found'
                }
              </span>
              <p>
                {showCompleted 
                  ? 'All your processes have completed successfully!' 
                  : 'All processes are currently completed or idle.'
                }
              </p>
            </div>
          ) : (
            <div className={styles.processList}>
              {filteredProcesses.map((process) => {
                const formattedStep = formatCurrentStep(process.current_step, process.status);
                
                return (
                  <div key={process.id} className={`${styles.processItem} ${styles[process.status]}`}>
                    <div className={styles.processHeader}>
                      <div className={styles.processInfo}>
                        <div className={styles.processType}>
                          {getProcessTypeText(process.process_type)}
                        </div>
                        {process.project_name && (
                          <div className={styles.projectName}>
                            Project: {process.project_name}
                          </div>
                        )}
                      </div>
                      <div className={styles.processStatus}>
                        {getStatusIcon(process.status)}
                        <span>{getStatusText(process.status)}</span>
                      </div>
                    </div>

                    <div className={styles.processDetails}>
                      {/* Only show current step for running processes */}
                      {process.status === 'running' && formattedStep && (
                        <div className={styles.currentStep}>
                          <i className="fas fa-cog fa-spin"></i>
                          <span>{formattedStep}</span>
                        </div>
                      )}
                      
                      {process.status === 'running' && (
                        <div className={styles.progress}>
                          <div className={styles.progressBar}>
                            <div 
                              className={styles.progressFill}
                              style={{ width: `${process.progress_percentage || 0}%` }}
                            ></div>
                          </div>
                          <span>{process.progress_percentage || 0}%</span>
                        </div>
                      )}

                      <div className={styles.processMeta}>
                        <div className={styles.metaItem}>
                          <i className="fas fa-clock"></i>
                          <span>Started: {formatDate(process.started_at)}</span>
                        </div>
                        {process.completed_at && (
                          <div className={styles.metaItem}>
                            <i className="fas fa-flag-checkered"></i>
                            <span>Completed: {formatDate(process.completed_at)}</span>
                          </div>
                        )}
                        <div className={styles.metaItem}>
                          <i className="fas fa-hourglass-half"></i>
                          <span>Duration: {formatDuration(process.started_at, process.completed_at)}</span>
                        </div>
                      </div>

                      {process.error_message && (
                        <div className={styles.errorMessage}>
                          <i className="fas fa-exclamation-triangle"></i>
                          <span>{process.error_message}</span>
                        </div>
                      )}

                      {/* {process.status === 'running' && (
                        <div className={styles.processActions}>
                          <button 
                            className={styles.cancelButton}
                            onClick={() => handleCancelProcess(process.id)}
                          >
                            <i className="fas fa-stop"></i>
                            Cancel Process
                          </button>
                        </div>
                      )} */}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundProcessModal; 